import { useEventBus as useEventBusCore, type EventBusKey } from '@vueuse/core'

// Helper type to create discriminated union from event map
type EventMapToDiscriminatedUnion<T extends Record<string, any>> = {
  [K in keyof T]: T[K] extends void ? { type: K } : { type: K; payload: T[K] }
}[keyof T]

// Cache handler maps by bus key so all calls with the same key share the same map
const handlerMapsCache = new Map<
  string | symbol | number,
  WeakMap<(event: any) => void, (event: any, payload: any) => void>
>()

// 2. Create a fully typed event bus wrapper
export function useEventBus<T extends Record<string, any>>(
  key: EventBusKey<keyof T> | string | number,
): {
  emit<K extends keyof T>(
    event: K,
    ...args: T[K] extends void ? [] : [T[K]]
  ): void
  on(handler: (event: EventMapToDiscriminatedUnion<T>) => void): () => void
  reset: () => void
  off: (handler: (event: EventMapToDiscriminatedUnion<T>) => void) => void
} {
  const bus = useEventBusCore<keyof T, T[keyof T]>(key)

  // Get or create handler map for this key (shared across all calls with same key)
  const cacheKey = key as string | symbol | number
  let handlerMap = handlerMapsCache.get(cacheKey) as
    | WeakMap<
        (event: EventMapToDiscriminatedUnion<T>) => void,
        (event: keyof T, payload: T[keyof T] | undefined) => void
      >
    | undefined

  if (!handlerMap) {
    handlerMap = new WeakMap<
      (event: EventMapToDiscriminatedUnion<T>) => void,
      (event: keyof T, payload: T[keyof T] | undefined) => void
    >()
    handlerMapsCache.set(cacheKey, handlerMap as any)
  }

  function emit<K extends keyof T>(
    event: K,
    ...args: T[K] extends void ? [] : [T[K]]
  ): void {
    bus.emit(event, args[0] as T[K])
  }

  function on(
    handler: (event: EventMapToDiscriminatedUnion<T>) => void,
  ): () => void {
    // Create the internal handler that converts to discriminated union
    const internalHandler = (
      event: keyof T,
      payload: T[keyof T] | undefined,
    ) => {
      const discriminatedEvent = (
        payload === undefined || payload === null
          ? { type: event }
          : { type: event, payload }
      ) as EventMapToDiscriminatedUnion<T>
      handler(discriminatedEvent)
    }

    // Store the mapping
    handlerMap!.set(handler, internalHandler)

    // Register with the bus and return cleanup function
    const stop = bus.on(internalHandler)
    return () => {
      handlerMap!.delete(handler)
      stop()
    }
  }

  function off(
    handler: (event: EventMapToDiscriminatedUnion<T>) => void,
  ): void {
    const internalHandler = handlerMap!.get(handler)
    if (internalHandler) {
      bus.off(internalHandler)
      handlerMap!.delete(handler)
    }
  }

  return {
    emit,
    on,
    reset: bus.reset,
    off,
  }
}
